import { useTranslation } from 'react-i18next'
import { IconButton, Tooltip, CircularProgress } from '@mui/material'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'
import { Button } from '@mui/material'
import { useBookmarks } from '@/hooks/useBookmarks'
import type { ContentType } from '@/types/api'

interface BookmarkButtonProps {
  contentType: ContentType
  entityId: number
}

/**
 * Icon-button variant — shown in the page header to the right of the title.
 * Filled bookmark when bookmarked, outline when not. Works offline.
 * Disabled while a toggle request is in-flight to prevent double-clicking.
 */
export function BookmarkIconButton({ contentType, entityId }: BookmarkButtonProps) {
  const { t } = useTranslation()
  const { isBookmarked, toggleBookmark, isToggling } = useBookmarks()
  const bookmarked = isBookmarked(contentType, entityId)

  return (
    <Tooltip title={bookmarked ? t('bookmark.removeBookmark') : t('bookmark.bookmarkThisPage')} placement="bottom">
      <span className="bookmark-icon-btn-wrapper">
        <IconButton
          className={`bookmark-icon-btn${bookmarked ? ' bookmark-icon-btn--active' : ''}`}
          onClick={() => toggleBookmark(contentType, entityId)}
          disabled={isToggling}
          aria-pressed={bookmarked}
          aria-label={bookmarked ? t('bookmark.removeBookmark') : t('bookmark.bookmarkThisPage')}
          size="medium"
        >
          {isToggling ? (
            <CircularProgress size={20} color="inherit" />
          ) : bookmarked ? (
            <BookmarkIcon fontSize="medium" />
          ) : (
            <BookmarkBorderIcon fontSize="medium" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  )
}

/**
 * Legacy text button — kept for backward compatibility but no longer used in headers.
 * Works offline.
 */
export function BookmarkButton({ contentType, entityId }: BookmarkButtonProps) {
  const { t } = useTranslation()
  const { isBookmarked, toggleBookmark, isToggling } = useBookmarks()
  const bookmarked = isBookmarked(contentType, entityId)

  return (
    <Button
      className={`bookmark-btn${bookmarked ? ' bookmarked' : ''}`}
      variant={bookmarked ? 'contained' : 'outlined'}
      onClick={() => toggleBookmark(contentType, entityId)}
      disabled={isToggling}
      title={bookmarked ? t('bookmark.removeBookmark') : t('bookmark.bookmarkThis')}
      aria-pressed={bookmarked}
      aria-label={bookmarked ? t('bookmark.removeBookmark') : t('bookmark.bookmarkThisContent')}
    >
      {bookmarked ? `🔖 ${t('bookmark.bookmarked')}` : `🔖 ${t('bookmark.bookmark')}`}
    </Button>
  )
}
